import { type PropsWithChildren, createContext, use, useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";

import type { z } from "zod";
import { Instance } from "./model";
import { fullPageCentred } from "./styles";

const REGEX_UUID = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;

// biome-ignore lint: null
export const InstanceIdContext = createContext<{ instanceId: string }>(null!!!);

export type InstanceType = z.infer<typeof Instance>;

// biome-ignore lint: null
export const InstanceContext = createContext<InstanceType>(null!!!);

const InstanceForm = () => {
  const [_, navigate] = useLocation();
  const [instanceId, setInstanceId] = useState("");

  const isValidInstanceID = instanceId.match(REGEX_UUID);
  const switchInstance = () => navigate(`/${instanceId}`);

  return (
    <div style={fullPageCentred}>
      <fieldset className="fieldset w-xs bg-base-200 border border-base-300 p-4 rounded-box">
        <label className="fieldset-label" htmlFor="instance-id-input">
          Instance ID
        </label>
        <input
          id="instance-id-input"
          type="text"
          className="input"
          placeholder="00000000-0000-0000-0000-000000000000"
          value={instanceId}
          onChange={(event) => setInstanceId(event.target.value)}
        />

        <button type="button" className="btn btn-soft mt-3" onClick={switchInstance} disabled={!isValidInstanceID}>
          Select
        </button>
      </fieldset>
    </div>
  );
};

export const InstanceIdProvider = (props: PropsWithChildren) => {
  const [match, params] = useRoute("/:instanceId/*?");

  // assume any uuid is valid, if instance doesn't exist then some other api call will surface the error
  if (!match || !params || !params.instanceId || !params.instanceId.match(REGEX_UUID)) {
    return <InstanceForm />;
  }

  return <InstanceIdContext value={{ instanceId: params.instanceId }}>{props.children}</InstanceIdContext>;
};

export const InstanceProvider = (props: PropsWithChildren) => {
  const { instanceId } = use(InstanceIdContext);
  const [instance, setInstance] = useState<InstanceType>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    setTimeout(async () => {
      try {
        setLoading(true);
        const resp = await fetch(`/api/instance/${instanceId}`);
        setInstance(Instance.parse(await resp.json()));
      } catch (e) {
        console.error(e);
        setError("something went horribly wrong");
      } finally {
        setLoading(false);
      }
    });
  }, [instanceId]);

  if (loading) {
    return (
      <div style={fullPageCentred}>
        <span className="loading loading-infinity loading-xl" />
      </div>
    );
  }

  if (!instance || error) {
    return <pre>{error || "something went horribly wrong"}</pre>;
  }

  return <InstanceContext value={instance}>{props.children}</InstanceContext>;
};
